import { Check, TriangleAlert } from "lucide-react";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import { getSupabaseClient } from "../../atomic-crm/providers/supabase/supabase";
import type { DuplicatePair } from "./useDuplicates";

interface ContactRow {
  id: number;
  first_name: string | null;
  last_name: string | null;
  title: string | null;
  email_jsonb: { email: string }[] | null;
  phone_jsonb: { number: string }[] | null;
  city: string | null;
  do_not_contact: boolean | null;
  sms_consent: string | null;
  email_consent: string | null;
  first_seen: string | null;
  last_seen: string | null;
}

const emails = (c: ContactRow) =>
  (c.email_jsonb ?? []).map((e) => e.email).filter(Boolean);
const phones = (c: ContactRow) =>
  (c.phone_jsonb ?? []).map((p) => p.number).filter(Boolean);

const Row = ({
  label,
  keep,
  merge,
}: {
  label: string;
  keep: string;
  merge: string;
}) => {
  // Highlight only where the two records actually differ, that is where the
  // user's attention needs to go.
  const differs = keep !== merge && merge !== ", ";
  return (
    <tr className="border-b border-border-subtle last:border-0">
      <td className="py-1.5 pr-3 align-top text-[12px] text-muted-foreground">
        {label}
      </td>
      <td className="py-1.5 pr-3 align-top text-[13px] font-medium text-foreground">
        {keep}
      </td>
      <td
        className={cn(
          "py-1.5 align-top text-[13px]",
          differs ? "text-foreground" : "text-muted-foreground",
        )}
      >
        {merge}
      </td>
    </tr>
  );
};

/**
 * Side-by-side comparison with an explicit winner.
 *
 * The naming is deliberate: "Keep" and "Merge & delete", never "A" and "B".
 * The server takes `p_winner_id` / `p_loser_id` and the old upstream function
 * took them in the opposite order, so ambiguity here would be expensive, * this screen never lets the user wonder which record disappears.
 */
export const MergePreview = ({
  pair,
  onDone,
}: {
  pair: DuplicatePair;
  onDone: (message: string) => void;
}) => {
  const [winnerId, setWinnerId] = useState<number>(pair.contact_a);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const { data, isPending } = useQuery<ContactRow[]>({
    queryKey: ["merge-preview", pair.contact_a, pair.contact_b],
    queryFn: async () => {
      const { data, error } = await getSupabaseClient()
        .from("contacts")
        .select(
          "id, first_name, last_name, title, email_jsonb, phone_jsonb, city, do_not_contact, sms_consent, email_consent, first_seen, last_seen",
        )
        .in("id", [pair.contact_a, pair.contact_b]);
      if (error) throw new Error(error.message);
      return (data ?? []) as ContactRow[];
    },
  });

  if (isPending || !data || data.length < 2) {
    return <p className="text-[13px] text-muted-foreground">Loading…</p>;
  }

  const winner = data.find((c) => c.id === winnerId)!;
  const loser = data.find((c) => c.id !== winnerId)!;

  const mergedEmails = Array.from(
    new Set([...emails(winner), ...emails(loser)].map((e) => e.toLowerCase())),
  );
  const mergedPhones = Array.from(
    new Set(
      [...phones(winner), ...phones(loser)].map((p) => p.replace(/\D/g, "")),
    ),
  );
  const willBeBlocked =
    Boolean(winner.do_not_contact) || Boolean(loser.do_not_contact);

  const merge = async () => {
    setBusy(true);
    setFailure(null);
    const { error } = await getSupabaseClient().rpc("merge_contacts", {
      p_winner_id: winnerId,
      p_loser_id: loser.id,
    });
    setBusy(false);
    if (error) {
      setFailure(error.message);
      return;
    }
    onDone(`Merged into ${winner.first_name} ${winner.last_name}`);
  };

  const name = (c: ContactRow) =>
    `${c.first_name ?? ""} ${c.last_name ?? ""}`.trim() || `#${c.id}`;

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <div>
        <p className="mb-2 text-[13px] text-muted-foreground">
          Which record should be kept? The other is merged into it and then
          deleted.
        </p>
        <div className="grid gap-2 sm:grid-cols-2">
          {data.map((candidate) => (
            <button
              key={candidate.id}
              onClick={() => setWinnerId(candidate.id)}
              className={cn(
                "flex items-center gap-2 rounded-md border px-3 py-2 text-left transition-colors",
                candidate.id === winnerId
                  ? "border-primary bg-primary/5"
                  : "border-input hover:bg-accent",
              )}
            >
              <span
                className={cn(
                  "flex size-4 shrink-0 items-center justify-center rounded-full border",
                  candidate.id === winnerId
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-input",
                )}
              >
                {candidate.id === winnerId ? (
                  <Check className="size-2.5" />
                ) : null}
              </span>
              <span className="min-w-0">
                <span className="block truncate text-[13px] font-medium text-foreground">
                  {name(candidate)}
                </span>
                <span className="block text-[11.5px] text-muted-foreground">
                  {candidate.id === winnerId
                    ? "Keep this one"
                    : "Merge & delete"}
                </span>
              </span>
            </button>
          ))}
        </div>
      </div>

      <div className="-mx-1 overflow-x-auto px-1">
        <table className="w-full min-w-[420px] border-collapse">
          <thead>
            <tr>
              <th className="pb-1.5 text-left text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground" />
              <th className="pb-1.5 text-left text-[11px] font-semibold uppercase tracking-[0.06em] text-emerald-700">
                Keeping
              </th>
              <th className="pb-1.5 text-left text-[11px] font-semibold uppercase tracking-[0.06em] text-destructive">
                Deleting
              </th>
            </tr>
          </thead>
          <tbody>
            <Row label="Name" keep={name(winner)} merge={name(loser)} />
            <Row
              label="Title"
              keep={winner.title || ", "}
              merge={loser.title || ", "}
            />
            <Row
              label="Email"
              keep={emails(winner).join(", ") || ", "}
              merge={emails(loser).join(", ") || ", "}
            />
            <Row
              label="Phone"
              keep={phones(winner).join(", ") || ", "}
              merge={phones(loser).join(", ") || ", "}
            />
            <Row
              label="City"
              keep={winner.city || ", "}
              merge={loser.city || ", "}
            />
          </tbody>
        </table>
      </div>

      <div className="rounded-md border border-border bg-muted/40 px-3 py-2.5">
        <p className="mb-1 text-[12px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
          After merging
        </p>
        <ul className="flex flex-col gap-0.5 text-[12.5px] text-foreground">
          <li>
            <span className="text-muted-foreground">Emails:</span>{" "}
            {mergedEmails.join(", ") || "none"}
          </li>
          <li>
            <span className="text-muted-foreground">Phones:</span>{" "}
            {mergedPhones.length} kept
          </li>
          <li className="text-muted-foreground">
            All deals, jobs, estimates, invoices, notes, tasks, files and
            conversations move to {name(winner)}.
          </li>
          {willBeBlocked ? (
            <li className="mt-1 flex items-start gap-1.5 text-amber-800">
              <TriangleAlert className="mt-0.5 size-3.5 shrink-0" />
              {/* Consent is the one field where the STRICTER value wins, so a
                  merge can never quietly re-enable outreach. */}
              <span>
                One of these contacts is marked do-not-contact, so the merged
                record stays do-not-contact.
              </span>
            </li>
          ) : null}
        </ul>
      </div>

      {failure ? (
        <p className="text-[12.5px] text-destructive">{failure}</p>
      ) : null}

      <div className="flex flex-wrap justify-end gap-2">
        <Button
          variant="outline"
          size="sm"
          disabled={busy}
          onClick={() => onDone("")}
        >
          Cancel
        </Button>
        <Button size="sm" disabled={busy} onClick={merge}>
          {busy ? "Merging…" : `Merge into ${name(winner)}`}
        </Button>
      </div>

      <p className="text-[11.5px] text-muted-foreground">
        A copy of the deleted record is written to the audit log, so this can be
        reconstructed if it turns out to be wrong.
      </p>
    </div>
  );
};
